import { Injectable } from '@angular/core';
import { CoresService } from './cores.service';
import { CoreService } from './core.service';
import _ from 'lodash';
import { Subject } from 'rxjs';

export interface MapEntity {
  id: string;
  name: string;
  layerPath?: string;
  cesiumEntity: any;
  showIn3d: boolean;
  showInLayersUI(): boolean;
  getCesiumEntity(): any;
  show(): void;
  hide(): void;
  goToEntity(): void;
  goToEntityOnSelection(): boolean;
  openEntityWindownOnSelection(): boolean;
  getDetailsWindow(): string;
  getWindowId(): string;
  isPrimitive(): boolean;
  drawPolygon?(): void;
  removePolygon?(): void;
  setAddedToMap(added: boolean): void;
  setDataSourceKey(key: string): void;
  getId(): string;
  isVisible(): boolean;
  dataSourceKey?: string;
  arcLiveMapEntity?: MapEntity;
}

@Injectable({
  providedIn: 'root'
})
export class DataContextService {
  private entityAddedListener: { [key: string]: (entity: any) => void } = {};
  private dataSources: { [key: string]: any } = {};
  private idToDataSource: { [key: string]: string } = {};
  public entitiesChanged$ = new Subject<void>();
  private entityToMapEntity = new Map<any, MapEntity>();

  constructor(private core: CoresService) {}

  addMapEntity(mapEntity: any, dataSourceKey?: string): void {
    const entity = mapEntity.getCesiumEntity();
    if (!mapEntity.isPrimitive() && (!dataSourceKey || !this.dataSources[dataSourceKey])) {
      const existing = this.core.getViewer().entities.getById(mapEntity.getId());
      if (existing) {
        this.core.getViewer().entities.remove(existing);
      }
    }

    if (mapEntity.showIn3d) {
      if (dataSourceKey && this.dataSources[dataSourceKey]) {
        mapEntity.setDataSourceKey(dataSourceKey);
        this.idToDataSource[mapEntity.getId()] = dataSourceKey;
        this.dataSources[dataSourceKey].entities.add(entity);
      } else if (mapEntity.isPrimitive()) {
        this.core.getViewer().scene.primitives.add(entity);
      } else {
        this.core.getViewer().entities.add(entity);
      }

      if (mapEntity.layerPath?.includes('Safe Zones')) {
        mapEntity.drawPolygon();
      }
    }

    mapEntity.setAddedToMap(true);
    this.fireEntityAddedEvent(mapEntity);
    this.entitiesChanged$.next();
  }
  addMapEntitytest(mapEntity: any, dataSourceKey?: string): void {
   
    const entity = mapEntity.getCesiumEntity();

    if (mapEntity.showIn3d) {
      if (dataSourceKey && this.dataSources[dataSourceKey]) {
        mapEntity.setDataSourceKey(dataSourceKey);
        this.idToDataSource[mapEntity.getId()] = dataSourceKey;
        this.dataSources[dataSourceKey].entities.add(entity);
      } else if (mapEntity.isPrimitive()) {
        this.core.getViewer().scene.primitives.add(entity);
      } else {
        this.core.getViewer().entities.add(entity);
      }

      if (mapEntity.layerPath?.includes('Safe Zones')) {
        mapEntity.drawPolygon();
      }
    }

    //mapEntity.setAddedToMap(true);
    this.fireEntityAddedEvent(mapEntity);
  }


  removeMapEntity(mapEntity: any, deleteEntityFromMemory = false, dataSourceKey?: string): void {
    if (!mapEntity) return;

    const entity = mapEntity.getCesiumEntity();
    if (mapEntity.isPrimitive()) {
      this.core.getViewer().scene.primitives.remove(entity);
    } else if (dataSourceKey) {
      delete this.idToDataSource[mapEntity.getId()];
      this.dataSources[dataSourceKey]?.entities?.remove(entity);
    } else {
      this.core.getViewer().entities.remove(entity);
    }

    mapEntity.setAddedToMap(false);

    if (mapEntity.layerPath?.includes('Safe Zones')) {
      mapEntity.removePolygon();
    }

    if (deleteEntityFromMemory) {
      mapEntity = null;
    }
  }

  getMapEntity(id: string): any {
    if (this.idToDataSource[id]) {
      return this.dataSources[this.idToDataSource[id]].entities._entities._hash[id];
    } else if (this.core.getViewer().entities._entities._hash[id]) {
      return this.core.getViewer().entities._entities._hash[id];
    }

    const primitives = this.core.getViewer().scene.primitives._primitives;
    return primitives.find((p: any) => p.arcLiveMapEntity?.id === id);
  }

  isMapEntityOnMap(id: string): boolean {
    if (this.idToDataSource[id]) return true;
    if (this.core.getViewer().entities._entities._hash[id]) return true;

    return this.core.getViewer().scene.primitives._primitives.some(
      (p: any) => p.arcLiveMapEntity?.id === id
    );
  }

  isCesiumEntityPresent(id: string): boolean {
    const viewer = this.core.getViewer();
    if (!viewer) return false;

    try {
      const byId = viewer.entities?.getById?.(id);
      if (byId) return true;
    } catch {
    }
    if (viewer.entities?._entities?._hash?.[id]) return true;

    for (const ds of Object.values(this.dataSources)) {
      try {
        const dsById = ds?.entities?.getById?.(id);
        if (dsById) return true;
      } catch {
      }
      if (ds?.entities?._entities?._hash?.[id]) return true;
    }

    try {
      return viewer.scene?.primitives?._primitives?.some(
        (p: any) => p.arcLiveMapEntity?.id === id
      );
    } catch {
      return false;
    }
  }

  getEntities(): any[] {
    let allEntities: any[] = [];
    // Main viewer entities
    allEntities = allEntities.concat(this.core.getViewer().entities._entities._array);
    // All registered dataSources
    Object.values(this.dataSources).forEach(ds => {
      if (ds.entities && ds.entities._array) {
        allEntities = allEntities.concat(ds.entities._array);
      }
    });
    // Primitives
    allEntities = allEntities.concat(this.core.getViewer().scene.primitives._primitives);
    return allEntities;
  }

  addMapEntityAddedListener(key: string, listener: (entity: any) => void): void {
    this.entityAddedListener[key] = listener;
  }

  removeMapEntityAddedListener(key: string): void {
    delete this.entityAddedListener[key];
  }

  private fireEntityAddedEvent(mapEntity: any): void {
    Object.values(this.entityAddedListener).forEach((listener) => listener(mapEntity));
  }

  addDataSource(key: string, datasource: any): void {
    this.dataSources[key] = datasource;
    const viewer = this.core.getViewer();
    if (viewer) {
      viewer.dataSources.add(datasource);
    }
  }

  initialize(): void {
    Object.values(this.dataSources).forEach((ds) => {
      this.core.getViewer().dataSources.add(ds);
    });
  }

  getDevicemapEntityByDeviceId(deviceId: string): any {
    const devices = this.dataSources['incident-devices']?._entityCollection._entities._array || [];
    return devices.find(
      (o: any) => 
        o.arcLiveMapEntity?.arcAngelId === deviceId
    )?.arcLiveMapEntity;
  }

  getDevicemapEntityAll(): Record<string, any> {
    const result: Record<string, any> = {};
    const devices = this.dataSources['incident-devices']?._entityCollection._entities._array || [];

    devices.forEach((o: any) => {
      result[o.arcLiveMapEntity.arcAngelId] = o.arcLiveMapEntity;
    });

    return result;
  }

  public registerEntityMapping(entity: any, mapEntity: MapEntity) {
    this.entityToMapEntity.set(entity, mapEntity);
  }

  public getMapEntityForCesiumEntity(entity: any): MapEntity | undefined {
    return this.entityToMapEntity.get(entity);
  }
}
